import type { ApiError } from './types';

/**
 * Текст, который показывается, когда сервер вернул ошибку без распознаваемого
 * тела. Экспортируется, чтобы потребитель мог отличить настоящее сообщение
 * бэкенда от этого fallback'а и при желании подменить его на свой UX-текст.
 */
export const UNKNOWN_SERVER_ERROR_MESSAGE = 'Неизвестная ошибка сервера';

export type ParsedApiError = {
  message: string;
  fields?: Record<string, string[]>;
};

/**
 * Разбирает тело ошибки бэкенда. Поддерживает четыре формата:
 *
 * - `{ "detail": "Сообщение" }` — generic, `non_field_errors`, `@api_view`;
 * - `{ "message": "Сообщение" }` — вьюхи не по DRF-схеме (429 от BACK-732);
 * - `{ "<field>": ["err1", "err2"] }` — field-level валидация DRF-сериализатора;
 * - `{ "payload": [{ loc, msg, type }] }` — вьюхи на pydantic (DE-662).
 *
 * `message` содержит только тексты ошибок, без технических имён полей: это то,
 * что увидит пользователь. Имена полей уходят в `fields` — для подсветки
 * конкретных полей формы.
 *
 * Никогда не возвращает `undefined` в `message`. Именно этим отличался
 * applications: там подставлялся `data.detail` без проверки, и ответ без
 * `detail` показывал пользователю строку `undefined`.
 */
export const parseApiError = (data: ApiError | undefined): ParsedApiError => {
  if (!data) return { message: UNKNOWN_SERVER_ERROR_MESSAGE };

  if (typeof data.detail === 'string' && data.detail) {
    return { message: data.detail };
  }

  // Вьюхи, отвечающие не по DRF-схеме, кладут готовый текст в `message`.
  // Так отвечает 429 от `/ai-reviews/fast-review` (BACK-732):
  // `{ error: 'rate_limit_exceeded', message: 'Ваш текущий план…' }`.
  // Проверка на строку обязательна: в field-level ответе DRF ключ `message`
  // был бы массивом сообщений — его разбирает ветка ниже, а не эта.
  if (typeof data.message === 'string' && data.message) {
    return { message: data.message };
  }

  if (Array.isArray(data.payload) && data.payload.length > 0) {
    const pydanticFields: Record<string, string[]> = {};
    const pydanticMessages: string[] = [];
    for (const item of data.payload) {
      if (!item || typeof item.msg !== 'string') continue;
      // Имя поля — последний строковый сегмент `loc`: `['body','inn'] → 'inn'`.
      // Без него остаётся только текст (например, `loc: [0]` для позиции в
      // списке, которой в форме нет).
      const locField = Array.isArray(item.loc)
        ? item.loc.findLast(
            (segment): segment is string => typeof segment === 'string',
          )
        : undefined;
      if (locField !== undefined) {
        (pydanticFields[locField] ??= []).push(item.msg);
      }
      pydanticMessages.push(item.msg);
    }
    if (pydanticMessages.length > 0) {
      return {
        message: pydanticMessages.join(' '),
        fields:
          Object.keys(pydanticFields).length > 0 ? pydanticFields : undefined,
      };
    }
  }

  const fields: Record<string, string[]> = {};
  const userMessages: string[] = [];

  for (const [key, value] of Object.entries(data)) {
    if (key === 'detail') continue;
    if (Array.isArray(value) && value.length > 0) {
      const stringErrors = value.filter(
        (item): item is string => typeof item === 'string',
      );
      if (stringErrors.length === 0) continue;
      fields[key] = stringErrors;
      // Имя поля (`end_at`, `target_size`) в `message` намеренно НЕ попадает:
      // это техническое имя бэкенда. Оно доступно через `fields`.
      userMessages.push(...stringErrors);
    }
  }

  if (userMessages.length > 0) {
    return { message: userMessages.join(' '), fields };
  }

  return { message: UNKNOWN_SERVER_ERROR_MESSAGE };
};

/**
 * Есть ли в сообщении что показывать пользователю.
 *
 * Нужно там, где приложение подставляет свой, более полезный текст: «возможно,
 * такой аккаунт уже существует», «не удалось создать заявку». Раньше эти места
 * опирались на falsy — в `message` приходила пустая строка. Теперь вместо неё
 * приходит `UNKNOWN_SERVER_ERROR_MESSAGE`, и проверку надо задавать явно:
 *
 *   isMeaningfulApiMessage(errorMessage) ? errorMessage : 'своя подсказка'
 *
 * Пустую строку обратно вернуть нельзя — это и был исходный баг: «ошибка есть,
 * а какая, не скажем».
 */
export const isMeaningfulApiMessage = (message?: string): boolean =>
  Boolean(message) && message !== UNKNOWN_SERVER_ERROR_MESSAGE;
